import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import Modal from 'src/components/modal/Modal';
import useSendEdi855Transaction from 'src/api/temp/famous/useSendEdi855Transaction';
import useSyncOrderConfirmation from 'src/api/temp/famous/useSyncOrderConfirmation';
import React, { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { orpc } from 'src/api/rpc/client';
import {
  HourglassIcon,
  ArrowsClockwiseIcon,
  CaretDownIcon,
  CaretUpIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner';
import SendEdi855Button from './SendEdi855Button';
import type { FamousOrderConfirmationDTO } from '@erp/rpc';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from 'src/utils/user';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { T, Var, useGT } from 'gt-react';

interface Props {
  orderConfirmation: FamousOrderConfirmationDTO;
  onClose: (refetch?: boolean) => void;
  isFetching?: boolean;
}

const OrderConfirmationModal: React.FC<Props> = ({
  orderConfirmation,
  onClose,
}) => {
  const gt = useGT();
  const [showRawData, setShowRawData] = useState(false);
  const [showForceRefreshDialog, setShowForceRefreshDialog] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdminUser = isAdmin(currentUser, viewMode);
  const queryClient = useQueryClient();

  const hasBeenSent = !!orderConfirmation.edi855SentAt;

  // Keep the modal open after send so the user can watch the status update
  // (pending → sent/error). Invalidate both the detail query (drives the
  // status badge in this modal) and every paginated list query (the
  // background table) — `.list.key()` is the prefix-level key that
  // matches all input variants.
  const invalidateOrderConfirmationQueries = () => {
    queryClient.invalidateQueries({
      queryKey: orpc.edi.orderConfirmations.get.queryOptions({
        input: { orderConfirmationId: orderConfirmation.id },
      }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: orpc.edi.orderConfirmations.list.key(),
    });
  };

  const sendEdi855Transaction = useSendEdi855Transaction(
    invalidateOrderConfirmationQueries,
    invalidateOrderConfirmationQueries,
  );

  const syncOrderConfirmation = useSyncOrderConfirmation(
    orderConfirmation.id,
    invalidateOrderConfirmationQueries,
  );

  const handleSendEdi = (force = false) => {
    sendEdi855Transaction.mutate({
      orderConfirmationId: orderConfirmation.id,
      force,
    });
  };

  const handleSyncOrder = () => {
    // If the 855 was already sent, an admin can force-refresh — but only
    // after confirming. Non-admins never hit this path (button disabled).
    if (hasBeenSent) {
      setShowForceRefreshDialog(true);
      return;
    }
    syncOrderConfirmation.mutate({});
  };

  const handleConfirmForceRefresh = () => {
    setShowForceRefreshDialog(false);
    syncOrderConfirmation.mutate({ force: true });
  };

  const handleRefreshStatus = () => {
    // Refresh both caches so the badge in this modal and the row in the
    // background table stay in sync.
    invalidateOrderConfirmationQueries();
  };

  const orderDetails = orderConfirmation.orderDetails as Array<
    Record<string, unknown>
  > | null;
  const firstOrder = orderDetails?.[0] as Record<string, unknown> | undefined;
  const customer = firstOrder?.customer as
    Record<string, string | null> | undefined;
  const shipTo = firstOrder?.shipTo as
    Record<string, string | null> | undefined;
  const carrier = firstOrder?.carrier as
    Record<string, string | null> | undefined;
  const lines = (firstOrder?.lines as Array<Record<string, unknown>>) ?? [];
  const itemLines = lines.filter((line) => line.type === '1' && line.name);

  return (
    <Modal
      maxWidth="xl"
      open={true}
      title={
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          gap={2}
        >
          <MDTypography variant="h6">
            <T>
              Famous Order Confirmation #
              <Var>{orderConfirmation.famousOrderId}</Var>
            </T>
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={1} sx={{ mr: 5 }}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <MDButton
                    variant="text"
                    color="dark"
                    onClick={handleSyncOrder}
                    disabled={
                      (hasBeenSent && !isAdminUser) ||
                      syncOrderConfirmation.isPending
                    }
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    {syncOrderConfirmation.isPending ? (
                      <HourglassIcon size={24} />
                    ) : (
                      <ArrowsClockwiseIcon size={24} />
                    )}
                  </MDButton>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {hasBeenSent && !isAdminUser
                  ? gt(
                      'Cannot refresh: this order confirmation has already been sent. Refresh is only available before send.',
                    )
                  : hasBeenSent && isAdminUser
                    ? gt(
                        'Admin: force-refresh this already-sent order confirmation (requires confirmation)',
                      )
                    : gt('Refresh order data from Famous')}
              </TooltipContent>
            </Tooltip>
          </MDBox>
        </MDBox>
      }
      closeable
      onCloseButton={{
        text: gt('Close', { context: 'button label to close a modal' }),
        onClick: () => onClose(),
        disabled:
          sendEdi855Transaction.isPending || syncOrderConfirmation.isPending,
      }}
    >
      <div className="relative grid h-full grid-cols-12 gap-6">
        {/* Loading overlay when syncing */}
        {syncOrderConfirmation.isPending && (
          <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-white/80">
            <LoadingSpinner
              size="xl"
              text={gt('Refreshing order data from Famous...')}
              centered
            />
          </div>
        )}

        {/* Error Details */}
        {orderConfirmation.orderFetchError && (
          <div className="col-span-12">
            <MDBox
              sx={{
                p: 2,
                backgroundColor: '#ffebee',
                borderRadius: 1,
                border: '1px solid #ffcdd2',
              }}
            >
              <MDTypography
                variant="caption"
                color="error.main"
                fontWeight="bold"
                sx={{ display: 'block', mb: 1 }}
              >
                <T>Error Details:</T>
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '10px', wordBreak: 'break-word' }}
              >
                {JSON.stringify(orderConfirmation.orderFetchError, null, 2)}
              </MDTypography>
            </MDBox>
          </div>
        )}

        {/* Left Panel - Order Details */}
        <div className="col-span-8 flex h-full flex-col overflow-auto">
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            p={2}
            sx={{ borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}
          >
            <MDTypography variant="h6" fontWeight="bold">
              <T>Order Details</T>
            </MDTypography>
          </MDBox>

          <MDBox sx={{ width: '100%', overflow: 'auto', p: 2 }}>
            {/* Order Summary */}
            {firstOrder && (
              <MDBox
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fafafa',
                }}
              >
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  <T>Order Summary</T>
                </MDTypography>

                <div className="grid grid-cols-2 gap-4">
                  {/* Order Information */}
                  <div>
                    <MDTypography
                      variant="subtitle2"
                      fontWeight="bold"
                      color="primary"
                      mb={1}
                    >
                      <T>Order Information</T>
                    </MDTypography>
                    <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                      <DetailRow
                        label={gt('Order #', {
                          context: 'label for order number field',
                        })}
                        value={firstOrder.orderNumber as string}
                      />
                      <DetailRow
                        label={gt('PO #', {
                          context: 'label for purchase order number field',
                        })}
                        value={firstOrder.purchaseOrderNumber as string}
                      />
                      <DetailRow
                        label={gt('Order Date', {
                          context: 'label for order date field',
                        })}
                        value={firstOrder.orderDate as string}
                      />
                      <DetailRow
                        label={gt('Invoice Date', {
                          context: 'label for invoice date field',
                        })}
                        value={firstOrder.invoiceDate as string}
                      />
                      <DetailRow
                        label={gt('ETA Date', {
                          context: 'label for estimated arrival date field',
                        })}
                        value={firstOrder.etaDate as string}
                      />
                      <DetailRow
                        label={gt('Status', {
                          context: 'label for order status field',
                        })}
                        value={firstOrder.orderStatus as string}
                      />
                    </MDBox>
                  </div>

                  {/* Customer Information */}
                  <div>
                    <MDTypography
                      variant="subtitle2"
                      fontWeight="bold"
                      color="primary"
                      mb={1}
                    >
                      <T>Customer Information</T>
                    </MDTypography>
                    <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                      <DetailRow
                        label={gt('Name', {
                          context: 'label for customer name field',
                        })}
                        value={customer?.description as string}
                      />
                      <DetailRow
                        label={gt('ID', {
                          context: 'label for customer ID field',
                        })}
                        value={customer?.id4 as string}
                      />
                      <DetailRow
                        label={gt('Address', {
                          context: 'label for customer address field',
                        })}
                        value={customer?.address as string}
                      />
                      <DetailRow
                        label={gt('City/State', {
                          context: 'label for customer city and state field',
                        })}
                        value={
                          customer
                            ? `${customer.city ?? ''}, ${customer.state ?? ''} ${customer.zip ?? ''}`
                            : undefined
                        }
                      />
                    </MDBox>
                  </div>

                  {/* Ship To */}
                  {shipTo && (
                    <div>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        <T>Ship To</T>
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        <DetailRow
                          label={gt('Name', {
                            context: 'label for ship-to name field',
                          })}
                          value={shipTo.name as string}
                        />
                        <DetailRow
                          label={gt('Address', {
                            context: 'label for ship-to address field',
                          })}
                          value={shipTo.street as string}
                        />
                        <DetailRow
                          label={gt('City/State', {
                            context: 'label for ship-to city and state field',
                          })}
                          value={`${shipTo.city ?? ''}, ${shipTo.state ?? ''} ${shipTo.zip ?? ''}`}
                        />
                        <DetailRow
                          label={gt('DUNS', {
                            context: 'label for DUNS number field',
                          })}
                          value={shipTo.DUNS as string}
                        />
                      </MDBox>
                    </div>
                  )}

                  {/* Carrier */}
                  {carrier && (
                    <div>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        <T>Carrier</T>
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        <DetailRow
                          label={gt('Name', {
                            context: 'label for carrier name field',
                          })}
                          value={carrier.name}
                        />
                        <DetailRow
                          label={gt('SCAC', {
                            context: 'label for carrier SCAC code field',
                          })}
                          value={carrier.SCAC}
                        />
                      </MDBox>
                    </div>
                  )}
                </div>
              </MDBox>
            )}

            {/* Line Items */}
            {itemLines.length > 0 && (
              <MDBox
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fafafa',
                }}
              >
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  <T>
                    Line Items (<Var>{itemLines.length}</Var>)
                  </T>
                </MDTypography>
                <MDBox sx={{ overflowX: 'auto' }}>
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="border-b-2 border-gray-200 text-left">
                        <th className="p-2">
                          <T context="table column header for line item number">
                            #
                          </T>
                        </th>
                        <th className="p-2">
                          <T context="table column header for product name">
                            Product
                          </T>
                        </th>
                        <th className="p-2">
                          <T context="table column header for SKU">SKU</T>
                        </th>
                        <th className="p-2 text-right">
                          <T context="table column header for quantity">Qty</T>
                        </th>
                        <th className="p-2 text-right">
                          <T context="table column header for unit price">
                            Price
                          </T>
                        </th>
                        <th className="p-2 text-right">
                          <T context="table column header for line total amount">
                            Amount
                          </T>
                        </th>
                        <th className="p-2">
                          <T context="table column header for unit of measure">
                            UOM
                          </T>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemLines.map((line, idx) => {
                        const product = (
                          line.product as Array<Record<string, unknown>>
                        )?.[0];
                        const translate = (
                          product?.translate as Array<Record<string, unknown>>
                        )?.[0];
                        return (
                          <tr key={idx} className="border-b border-gray-100">
                            <td className="p-2">{line.number as number}</td>
                            <td className="p-2">{line.name as string}</td>
                            <td className="p-2">
                              {(translate?.externalSku as string) ?? '-'}
                            </td>
                            <td className="p-2 text-right">
                              {line.quantity as number}
                            </td>
                            <td className="p-2 text-right">
                              ${(line.price as number)?.toFixed(2) ?? '-'}
                            </td>
                            <td className="p-2 text-right">
                              ${(line.saleAmount as number)?.toFixed(2) ?? '-'}
                            </td>
                            <td className="p-2">
                              {(line.unitOfMeasureEDI as string) ?? '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </MDBox>
              </MDBox>
            )}

            {/* Raw JSON Data */}
            <MDBox>
              <MDButton
                variant="text"
                color="dark"
                onClick={() => setShowRawData(!showRawData)}
                sx={{ mb: 1 }}
              >
                {showRawData ? (
                  <CaretUpIcon size={20} className="mr-1" />
                ) : (
                  <CaretDownIcon size={20} className="mr-1" />
                )}
                {showRawData
                  ? gt('Hide Raw Data', {
                      context: 'button label to collapse raw JSON data section',
                    })
                  : gt('Show Raw Data', {
                      context: 'button label to expand raw JSON data section',
                    })}
              </MDButton>
              {showRawData && (
                <MDBox
                  sx={{
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    maxHeight: '400px',
                    overflow: 'auto',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '11px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(orderDetails, null, 2)}
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </div>

        {/* Right Panel - EDI 855 Status & Actions */}
        <div className="col-span-4 flex flex-col">
          <MDBox
            p={2}
            sx={{ borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}
          >
            <MDTypography variant="h6" fontWeight="bold">
              <T>EDI 855 Status</T>
            </MDTypography>
          </MDBox>
          <MDBox p={2}>
            <SendEdi855Button
              orderConfirmation={orderConfirmation}
              sendEdi855Transaction={sendEdi855Transaction}
              onSendEdi={handleSendEdi}
              onRefreshStatus={handleRefreshStatus}
              isLoading={sendEdi855Transaction.isPending}
            />
          </MDBox>
        </div>
      </div>
      <ConfirmDialog
        open={showForceRefreshDialog}
        onClose={() => setShowForceRefreshDialog(false)}
        onConfirm={handleConfirmForceRefresh}
        title={gt('Refresh already-sent order confirmation?')}
        message={(() => {
          const sentAt = orderConfirmation.edi855SentAt;
          const when = sentAt ? new Date(sentAt).toLocaleString() : null;
          return when
            ? gt(
                'This order confirmation (855) was already sent on {when}. Refreshing will re-fetch fresh data from Famous and clear the sent state so it can be re-transmitted. The previous send record will be lost.',
                { when },
              )
            : gt(
                'This order confirmation (855) was already sent. Refreshing will re-fetch fresh data from Famous and clear the sent state so it can be re-transmitted. The previous send record will be lost.',
              );
        })()}
        confirmText={gt('Refresh and clear sent state', {
          context:
            'button label to confirm force-refresh of already-sent order confirmation',
        })}
        dangerous
        loading={syncOrderConfirmation.isPending}
      />
    </Modal>
  );
};

// ============================================================================
// Helper Components
// ============================================================================

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string | undefined | null;
}) {
  if (!value) return null;
  return (
    <MDBox mb={0.5}>
      <MDTypography component="span" variant="caption" fontWeight="bold">
        {label}:
      </MDTypography>
      <MDTypography component="span" variant="caption" ml={0.5}>
        {value}
      </MDTypography>
    </MDBox>
  );
}

export default OrderConfirmationModal;
